import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth, axiosInstance as axios } from '../context/AuthContext';
import {
  FaUsers, FaArrowLeft, FaHome, FaUserTie, FaCalendarAlt, FaVideo, FaCheck
} from 'react-icons/fa';
import { useSubscription, FEATURES } from '../hooks/useSubscription';
import UpgradeModal from '../components/UpgradeModal';

import { API, TabButton } from './resources/resourcesConstants';
import { ProfessionalsTab } from './resources/ProfessionalsTab';
import { WebinarsTab } from './resources/WebinarsTab';
import { ConsultationsTab } from './resources/ConsultationsTab';
import { EditConsultationModal } from './resources/EditConsultationModal';
import { BookingModal } from './resources/BookingModal';
import { BrandLogo } from '../components/BrandLogo';

export default function Resources() {
  const navigate = useNavigate();
  useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasFeature } = useSubscription();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'professionals');
  const [professionals, setProfessionals] = useState([]);
  const [webinars, setWebinars] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [editingConsultation, setEditingConsultation] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [profsRes, webinarsRes, consultsRes] = await Promise.all([
        axios.get(`${API}/professionals`, { withCredentials: true }),
        axios.get(`${API}/webinars`, { withCredentials: true }),
        axios.get(`${API}/consultations`, { withCredentials: true })
      ]);

      setProfessionals(profsRes.data);
      setWebinars(webinarsRes.data);
      setConsultations(consultsRes.data);
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setSearchParams({ tab: activeTab }); }, [activeTab, setSearchParams]);

  const handleBookConsultation = (professional) => {
    setSelectedProfessional(professional);
    setShowBookingModal(true);
  };

  const handleRegisterWebinar = async (webinarId) => {
    try {
      await axios.post(`${API}/webinars/${webinarId}/register`, {}, { withCredentials: true });
      setWebinars(webinars.map(w =>
        w.webinar_id === webinarId ? { ...w, is_registered: true, registered_count: w.registered_count + 1 } : w
      ));
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to register');
    }
  };

  const handleUnregisterWebinar = async (webinarId) => {
    try {
      await axios.delete(`${API}/webinars/${webinarId}/register`, { withCredentials: true });
      setWebinars(webinars.map(w =>
        w.webinar_id === webinarId ? { ...w, is_registered: false, registered_count: w.registered_count - 1 } : w
      ));
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to unregister');
    }
  };

  const handleCancelConsultation = async (consultationId) => {
    if (!window.confirm('Are you sure you want to cancel this consultation?')) return;
    try {
      await axios.delete(`${API}/consultations/${consultationId}`, { withCredentials: true });
      setConsultations(consultations.filter(c => c.consultation_id !== consultationId));
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const handleEditConsultation = async (consultationId, updateData) => {
    try {
      const response = await axios.patch(`${API}/consultations/${consultationId}`, updateData, { withCredentials: true });
      setConsultations(consultations.map(c =>
        c.consultation_id === consultationId ? response.data : c
      ));
      setEditingConsultation(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update consultation');
    }
  };

  const filteredProfessionals = professionals.filter(p => {
    if (filterType && p.professional_type !== filterType) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return p.name.toLowerCase().includes(query) ||
             p.specialties.some(s => s.toLowerCase().includes(query)) ||
             (p.company && p.company.toLowerCase().includes(query));
    }
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading resources...</p>
        </div>
      </div>
    );
  }

  if (!hasFeature(FEATURES.RESOURCES_PROFESSIONALS)) {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="resources-page">
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          feature="resources_professionals"
          requiredTier="flourishing"
        />

        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-600 hover:text-gray-800 transition-colors flex items-center space-x-2"
              >
                <FaArrowLeft />
                <span>Dashboard</span>
              </button>
              <h1 className="text-2xl font-bold text-gray-800">Resources & Professionals</h1>
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-8 text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaUserTie className="w-10 h-10 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Professional Resources</h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Access our network of financial advisors, estate attorneys, family therapists,
              and other professionals to help your family thrive.
              Available exclusively on the Flourishing plan.
            </p>
            <ul className="text-left max-w-sm mx-auto mb-8 space-y-3">
              <li className="flex items-center gap-3 text-gray-700"><FaCheck className="text-amber-500" />Book 1-on-1 consultations</li>
              <li className="flex items-center gap-3 text-gray-700"><FaCheck className="text-amber-500" />Group family consultations</li>
              <li className="flex items-center gap-3 text-gray-700"><FaCheck className="text-amber-500" />Access webinars & resources</li>
              <li className="flex items-center gap-3 text-gray-700"><FaCheck className="text-amber-500" />Financial, legal & therapy experts</li>
            </ul>
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="px-8 py-4 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors text-lg"
            >
              🌳 Upgrade to Flourishing
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="resources-page">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/dashboard" className="flex items-center space-x-2 hover:opacity-80 transition">
                <BrandLogo className="w-9 h-9" testId="resources-logo" />
                <span className="text-xl font-bold text-gray-800 hidden sm:inline">Legacy Forge Family</span>
              </Link>
              <span className="text-gray-300">|</span>
              <button onClick={() => navigate('/dashboard')} className="text-gray-600 hover:text-gray-800">
                <FaArrowLeft className="text-xl" />
              </button>
              <h1 className="text-xl font-bold text-gray-800">Wealth Building Resources</h1>
            </div>

            <Link to="/" className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition text-xs sm:text-sm">
              <FaHome />
              <span>Home</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex space-x-1">
            <TabButton
              icon={<FaUserTie />}
              label="Professionals"
              active={activeTab === 'professionals'}
              onClick={() => setActiveTab('professionals')}
              count={professionals.length}
            />
            <TabButton
              icon={<FaVideo />}
              label="Webinars"
              active={activeTab === 'webinars'}
              onClick={() => setActiveTab('webinars')}
              count={webinars.filter(w => w.status === 'upcoming').length}
            />
            <TabButton
              icon={<FaCalendarAlt />}
              label="My Consultations"
              active={activeTab === 'consultations'}
              onClick={() => setActiveTab('consultations')}
              count={consultations.filter(c => c.status === 'pending' || c.status === 'confirmed').length}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="container mx-auto px-4 py-8">
        {activeTab === 'professionals' && (
          <ProfessionalsTab
            professionals={filteredProfessionals}
            filterType={filterType}
            setFilterType={setFilterType}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onBook={handleBookConsultation}
          />
        )}

        {activeTab === 'webinars' && (
          <WebinarsTab
            webinars={webinars}
            onRegister={handleRegisterWebinar}
            onUnregister={handleUnregisterWebinar}
          />
        )}

        {activeTab === 'consultations' && (
          <ConsultationsTab
            consultations={consultations}
            onCancel={handleCancelConsultation}
            onEdit={setEditingConsultation}
          />
        )}
      </main>

      {editingConsultation && (
        <EditConsultationModal
          consultation={editingConsultation}
          onClose={() => setEditingConsultation(null)}
          onSave={(updateData) => handleEditConsultation(editingConsultation.consultation_id, updateData)}
        />
      )}

      {showBookingModal && selectedProfessional && (
        <BookingModal
          professional={selectedProfessional}
          onClose={() => {
            setShowBookingModal(false);
            setSelectedProfessional(null);
          }}
          onSuccess={(newConsultation) => {
            setConsultations([newConsultation, ...consultations]);
            setShowBookingModal(false);
            setSelectedProfessional(null);
            setActiveTab('consultations');
          }}
        />
      )}
    </div>
  );
}
